import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import { type AgentMetadata, type Conversation } from './types';

/**
 * Creates minimal conversation-agent metadata when we know only the agent id.
 * This lets optimistic/local conversation state carry the conversation-associated
 * `conversation.agent.agent_id` before full backend metadata has hydrated. The
 * blank display fields are intentional placeholders; UI should resolve/display
 * full agent metadata separately instead of treating this as a complete agent.
 *
 * Returns `undefined` for missing/empty ids so we never seed a blank agent.
 */
export const createProvisionalConversationAgent = (agentId?: string): AgentMetadata | undefined => {
	if (!agentId) {
		return undefined;
	}

	return {
		agent_id: agentId,
		agent_named_id: agentId,
		agent_name: '',
		creator_type: '',
	};
};

/**
 * Resolve the conversation agent id used as the no-override routing fallback.
 *
 * Under the mention-agents experiment, the stored `conversation.agent.agent_id`
 * is the conversation agent when present; otherwise (or experiment-off) we fall
 * back to the caller intent supplied in `fallbackAgentId`.
 *
 * Keep the caller-id fallback until stored conversation agent ids are always
 * populated.
 */
export const resolveConversationAgentIdForRouting = ({
	conversation,
	fallbackAgentId,
}: {
	conversation: Conversation | undefined;
	fallbackAgentId: string;
}): string => {
	const storedConversationAgentId = conversation?.agent?.agent_id || undefined;
	return storedConversationAgentId && isExperimentEnabled('rovo_chat_mention_agents')
		? storedConversationAgentId
		: fallbackAgentId;
};
